import Empty from "antd/lib/empty";
import clsx from "clsx";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronLeft } from "@sol/uikit/core/icons/Arrows/ChevronLeft";
import { ChevronRight } from "@sol/uikit/core/icons/Arrows/ChevronRight";
import { Carousel } from "@sol/uikit/data-display/Carousel/Carousel";
import { Button } from "@sol/uikit/general/Button/Button";

import styles from "@/shared/carousel/carouselArrows.module.css";

import { Slide } from "./Slide";

type SwiperProps<T> = {
    items: T[];
    getItemKey: (item: T) => string;
    renderItem: (item: T, index: number) => ReactNode;
    className?: string;
    emptyMessage?: string;
    fallback?: ReactNode;
};

/*
    This function defines if the slide defined by index should be preloaded.
    slideCount: number of slides in the carousel
    activeIndex: index of the active slide
    index: index of the slide to check
    infinite: whether the carousel is infinite
    overFetch: number of slides to preload before and after the active slide

    returns: true if the slide should be preloaded, false otherwise
*/
const shouldPreload = (slideCount: number, activeIndex: number, index: number, infinite = true, overFetch = 1) => {
    if (!infinite) {
        return Math.abs(index - activeIndex) <= overFetch;
    }

    const distance = Math.min(Math.abs(index - activeIndex), slideCount - Math.abs(index - activeIndex));

    return distance <= overFetch;
};

export function Swiper<T>({ items = [], renderItem, getItemKey, className, emptyMessage, fallback }: SwiperProps<T>) {
    const [t] = useTranslation();
    const [activeSlide, setActiveSlide] = useState(0);

    if (items.length === 0) {
        return (
            <Empty
                className={clsx("m-ant-xxs mx-auto h-16", className)}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyMessage || t("entities.classroom.components.ClassroomsList.empty")}
            />
        );
    }

    const handleSlideChange = (from: number, to: number) => {
        setActiveSlide(to);
    };

    return (
        <div className={clsx("size-full", styles.carouselArrows)}>
            <Carousel
                dots={items.length > 1}
                swipeToSlide
                touchMove
                draggable={true}
                arrows={items.length > 1}
                dotPosition="bottom"
                prevArrow={
                    <Button size="small">
                        <ChevronLeft />
                    </Button>
                }
                nextArrow={
                    <Button size="small">
                        <ChevronRight />
                    </Button>
                }
                beforeChange={handleSlideChange}
            >
                {items.map((item, index) => {
                    return (
                        <Slide
                            key={getItemKey(item)}
                            shouldPreload={shouldPreload(items.length, activeSlide, index)}
                            fallback={fallback}
                        >
                            {renderItem(item, index)}
                        </Slide>
                    );
                })}
            </Carousel>
        </div>
    );
}
